import type { Manifest } from '@clawharbor/manifest';
import { createDemoApi } from './demo.js';
import type {
  AccountApi,
  LoginResponse,
  OnboardApi,
  Ownership,
  OwnershipChallenge,
  OwnershipStatus,
  OwnershipVerification,
  Principal,
  ProgressEvent,
  PublishResponse,
  StartResponse,
} from './types.js';

/**
 * The live onboarding client.
 *
 * This speaks the server that actually exists in apps/server/src/routes/onboard.ts,
 * which is richer than the four-field contract the console was specced against:
 *
 *   - SSE frames are NAMED (`job`, `log`, `phase`, `state`, `end`) and carry
 *     `{seq, at, kind, text}`, not `{phase, message, pct}`. A `log` frame's
 *     `text` is a raw crawler line — which is exactly what `parseProbeLine`
 *     reads, so the clearance ledger is populated by the real stream with no
 *     server-side change.
 *   - `phase` frames carry `"brand|40"`, i.e. name and percent in one string.
 *   - `GET /manifest` wraps the manifest in `{ok, draft, manifest}`.
 *   - `POST /publish` returns `{ok, urls: {web, mcp, …}}` and is authorised by
 *     EITHER the operator's publish secret in a header OR the `ch_session`
 *     cookie of the account that started the job.
 *
 * Both shapes are accepted at every seam. Where the server sends the documented
 * flat fields they are used as-is; where it sends its own richer shape that is
 * used instead. Neither side has to move for the other to work.
 */

/** apps/server: `CLAWHARBOR_RELOAD_HEADER`, lower-cased, default below. */
const DEFAULT_PUBLISH_HEADER = 'x-clawharbor-reload-secret';

function apiBase(): string {
  const meta = document.querySelector<HTMLMetaElement>('meta[name="clawharbor-api"]');
  const configured = meta?.content?.trim();
  if (configured) return configured.replace(/\/+$/, '');
  const override = new URLSearchParams(location.search).get('api');
  if (override && override !== 'demo') return override.replace(/\/+$/, '');
  return '';
}

function publishHeaderName(): string {
  const meta = document.querySelector<HTMLMetaElement>('meta[name="clawharbor-publish-header"]');
  return (meta?.content?.trim() || DEFAULT_PUBLISH_HEADER).toLowerCase();
}

/**
 * "The server answered and said no" — as opposed to "there is no server".
 *
 * The difference decides whether the console falls back to the recorded replay.
 * Falling back on a 400 would hide "robots.txt disallows everything for our
 * user agent" behind a cinema that crawls beautifully, which is the exact
 * dishonesty this product exists to avoid.
 */
export class ApiError extends Error {
  readonly status: number;
  /**
   * The API's own word for the refusal (`ownership_unproven`,
   * `third_party_requires_operator`, …), or null when the body had none. The
   * gate keys its "what to do next" on this rather than on the status, because
   * the same refusal has arrived as a 403 and as a 409 from different routes.
   */
  readonly code: string | null;
  constructor(message: string, status: number, code: string | null = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

async function readFailure(res: Response): Promise<{ message: string; code: string | null }> {
  const text = await res.text().catch(() => '');
  if (!text) return { message: `${res.status} ${res.statusText}`, code: null };
  try {
    const body = JSON.parse(text) as { error?: string; message?: string; code?: string };
    return {
      message: body.error ?? body.message ?? text.slice(0, 240),
      code: typeof body.code === 'string' ? body.code : null,
    };
  } catch {
    return { message: text.slice(0, 240), code: null };
  }
}

async function readError(res: Response): Promise<string> {
  return (await readFailure(res)).message;
}

async function refusal(res: Response): Promise<ApiError> {
  const { message, code } = await readFailure(res);
  return new ApiError(message, res.status, code);
}

/**
 * The session rides in the `ch_session` cookie, so every call the gate makes
 * on the account's behalf says so explicitly. `same-origin` rather than
 * `include`: the console is served by the host it talks to, and a configured
 * cross-origin API base gets no cookie — the browser would refuse the
 * credentialed CORS request anyway, and a silent 401 there is at least honest.
 */
const WITH_SESSION: RequestInit = { credentials: 'same-origin' };

/** `{kind:'log', text:'   PASS  x  rows=1 pass=100%'}` and friends → ProgressEvent. */
function normalizeFrame(raw: unknown): ProgressEvent | null {
  if (raw == null || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;

  // The server's opening/closing `job` frame: a whole public job record.
  if (typeof o['state'] === 'string' && ('progress' in o || 'phase' in o)) {
    const state = o['state'] as string;
    const out: ProgressEvent = {};
    if (typeof o['phase'] === 'string') out.phase = o['phase'];
    if (typeof o['progress'] === 'number') out.pct = o['progress'];
    if (typeof o['error'] === 'string' && o['error']) out.error = o['error'];
    if (state === 'succeeded') out.status = 'ready';
    else if (state === 'failed' || state === 'cancelled') out.status = 'failed';
    else if (state === 'running') out.status = 'running';
    else out.status = 'queued';
    return out;
  }

  const kind = typeof o['kind'] === 'string' ? o['kind'] : undefined;
  const text = typeof o['text'] === 'string' ? o['text'] : undefined;

  if (kind === 'phase' && text) {
    // "brand|40"
    const [name, pct] = text.split('|');
    const out: ProgressEvent = {};
    if (name) out.phase = name.trim();
    const n = Number(pct);
    if (Number.isFinite(n)) out.pct = n;
    return out;
  }

  if (kind === 'state' && text) {
    if (text === 'succeeded') return { status: 'ready' };
    if (text === 'failed' || text === 'cancelled') {
      return { status: 'failed', error: `the crawl ${text}` };
    }
    return { status: text === 'running' ? 'running' : 'queued' };
  }

  if (kind === 'log' && text) return { message: text };

  // The flat shape the console was specced against.
  const flat: ProgressEvent = {};
  if (typeof o['phase'] === 'string') flat.phase = o['phase'];
  if (typeof o['message'] === 'string') flat.message = o['message'];
  else if (text) flat.message = text;
  if (typeof o['pct'] === 'number') flat.pct = o['pct'];
  if (typeof o['status'] === 'string') flat.status = o['status'] as ProgressEvent['status'];
  if (typeof o['error'] === 'string') flat.error = o['error'];
  if (typeof o['capability'] === 'string') flat.capability = o['capability'];
  if (typeof o['verified'] === 'boolean') flat.verified = o['verified'];
  if (typeof o['rows'] === 'number') flat.rows = o['rows'];
  if (typeof o['passRate'] === 'number') flat.passRate = o['passRate'];
  if (typeof o['samples'] === 'number') flat.samples = o['samples'];
  return Object.keys(flat).length ? flat : null;
}

class LiveApi implements OnboardApi, AccountApi {
  readonly mode = 'live' as const;
  private readonly base = apiBase();

  /** The operator's own publish secret, typed into the gate. Never stored. */
  publishSecret = '';

  /**
   * Has anything on this API ever answered, at any status, on this page?
   *
   * This is the difference between "there is no onboarding API behind this
   * console" and "the API is right there and something went wrong on one
   * call" — and only the first of those may ever become a replayed demo. A
   * refusal sets it as surely as a success does: a 401 is a server answering.
   *
   * It is set by the three calls that can run before a crawl does — `me()` at
   * boot, `login()`, and `start()` itself. Nothing later needs to record it,
   * because `start()` is the only place the replay can be reached from.
   */
  reached = false;

  async start(url: string, ownership?: Ownership): Promise<StartResponse> {
    // With a session, the server records the job's account from the cookie;
    // that is what later lets the same person publish it without the secret.
    const res = await fetch(`${this.base}/api/onboard`, {
      ...WITH_SESSION,
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(ownership ? { url, ownership } : { url }),
    });
    this.reached = true;
    if (!res.ok) throw await refusal(res);
    const body = (await res.json()) as Partial<StartResponse>;
    if (!body.jobId) throw new Error('the onboard response carried no jobId');
    return { jobId: body.jobId, tenantId: body.tenantId ?? body.jobId };
  }

  events(
    jobId: string,
    handlers: {
      onEvent: (e: ProgressEvent) => void;
      onDone: () => void;
      onError: (message: string) => void;
    },
  ): () => void {
    const source = new EventSource(`${this.base}/api/onboard/${encodeURIComponent(jobId)}/events`);
    let settled = false;
    let terminal: 'done' | 'failed' | null = null;
    let failure = 'the crawl failed';

    const settle = (fn: () => void): void => {
      if (settled) return;
      settled = true;
      source.close();
      fn();
    };

    const handle = (raw: string): void => {
      const trimmed = raw.trim();
      if (!trimmed) return;
      let parsed: ProgressEvent | null;
      try {
        parsed = normalizeFrame(JSON.parse(trimmed));
      } catch {
        // Not JSON. Still information: show it rather than dropping it.
        parsed = { message: trimmed };
      }
      if (!parsed) return;
      handlers.onEvent(parsed);

      if (parsed.status === 'failed' || parsed.error) {
        terminal = 'failed';
        failure = parsed.error ?? failure;
        return;
      }
      // `ready` does NOT settle immediately: the server sends a final `job`
      // frame and then `end`, and closing on the first terminal signal would
      // race the manifest becoming readable.
      if (parsed.status === 'ready' || parsed.status === 'published') terminal = 'done';
    };

    const close = (): void => {
      if (terminal === 'failed') settle(() => handlers.onError(failure));
      else settle(handlers.onDone);
    };

    source.onmessage = (ev: MessageEvent<string>) => handle(ev.data);
    for (const name of ['log', 'phase', 'state', 'job', 'progress']) {
      source.addEventListener(name, (ev) => handle((ev as MessageEvent<string>).data));
    }
    source.addEventListener('end', close);
    source.addEventListener('done', close);

    source.onerror = () => {
      // EventSource reports a clean server-side close the same way it reports a
      // network failure. CONNECTING means it is retrying on its own and we must
      // not interfere; CLOSED means the browser gave up.
      if (source.readyState !== EventSource.CLOSED) return;
      if (terminal) close();
      else settle(() => handlers.onError('the progress stream closed unexpectedly'));
    };

    return () => settle(() => {});
  }

  async manifest(jobId: string): Promise<Manifest> {
    const res = await fetch(`${this.base}/api/onboard/${encodeURIComponent(jobId)}/manifest`);
    if (!res.ok) throw new ApiError(await readError(res), res.status);
    const body = (await res.json()) as { manifest?: Manifest } & Partial<Manifest>;
    // The server wraps it as {ok, draft, manifest}; the specced contract does not.
    if (body.manifest && typeof body.manifest === 'object') return body.manifest;
    if (body.capabilities && body.brand) return body as Manifest;
    throw new Error('the manifest response contained no manifest');
  }

  async publish(jobId: string): Promise<PublishResponse> {
    const headers: Record<string, string> = { 'content-type': 'application/json' };
    if (this.publishSecret) headers[publishHeaderName()] = this.publishSecret;

    const res = await fetch(`${this.base}/api/onboard/${encodeURIComponent(jobId)}/publish`, {
      ...WITH_SESSION,
      method: 'POST',
      headers,
      body: '{}',
    });
    if (!res.ok) {
      // The API's sentence is the whole answer; the gate adds the next step
      // from the code. A 401 with no code is the one case the server leaves
      // unnamed, and it means neither a session nor the secret arrived.
      const err = await refusal(res);
      if (res.status === 401 && !err.code) throw new ApiError(err.message, 401, 'unauthorized');
      throw err;
    }
    const body = (await res.json()) as {
      urls?: { web?: string; mcp?: string };
      webUrl?: string;
      mcpUrl?: string;
    };
    const webUrl = body.urls?.web ?? body.webUrl;
    const mcpUrl = body.urls?.mcp ?? body.mcpUrl;
    if (!webUrl || !mcpUrl) throw new Error('the publish response was missing a URL');
    return { webUrl, mcpUrl };
  }

  async me(): Promise<Principal | null> {
    const res = await fetch(`${this.base}/api/auth/me`, { ...WITH_SESSION, cache: 'no-store' });
    this.reached = true;
    if (!res.ok) return null;
    const body = (await res.json()) as { principal?: Principal };
    const p = body.principal;
    if (!p || (p.kind !== 'operator' && p.kind !== 'account')) return null;
    return p;
  }

  async login(email: string, next?: string): Promise<LoginResponse> {
    const res = await fetch(`${this.base}/api/auth/login`, {
      ...WITH_SESSION,
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(next ? { email, next } : { email }),
    });
    this.reached = true;
    if (!res.ok) throw await refusal(res);
    const body = (await res.json()) as { sent?: boolean; devLink?: string };
    return {
      sent: body.sent !== false,
      ...(typeof body.devLink === 'string' && body.devLink ? { devLink: body.devLink } : {}),
    };
  }

  async ownership(tenantId: string): Promise<OwnershipStatus> {
    const res = await fetch(`${this.base}/api/ownership/${encodeURIComponent(tenantId)}`, {
      ...WITH_SESSION,
      cache: 'no-store',
    });
    if (!res.ok) throw await refusal(res);
    const body = (await res.json()) as Partial<OwnershipStatus>;
    return { live: body.live === true, claim: body.claim ?? null, openChallenge: body.openChallenge ?? null };
  }

  async challenge(tenantId: string): Promise<OwnershipChallenge> {
    const res = await fetch(`${this.base}/api/ownership/${encodeURIComponent(tenantId)}/challenge`, {
      ...WITH_SESSION,
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: '{}',
    });
    if (!res.ok) throw await refusal(res);
    const body = (await res.json()) as Partial<OwnershipChallenge>;
    if (!body.challengeId || !body.token || !body.instructions || !body.next) {
      throw new Error('the challenge response carried no proofs');
    }
    return body as OwnershipChallenge;
  }

  async verify(tenantId: string, challengeId?: string): Promise<OwnershipVerification> {
    const res = await fetch(`${this.base}/api/ownership/${encodeURIComponent(tenantId)}/verify`, {
      ...WITH_SESSION,
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(challengeId ? { challengeId } : {}),
    });
    if (!res.ok) throw await refusal(res);
    const body = (await res.json()) as Partial<OwnershipVerification>;
    return {
      verified: body.verified === true,
      method: body.method ?? null,
      attempts: Array.isArray(body.attempts) ? body.attempts : [],
      ...(body.nextStep ? { nextStep: body.nextStep } : {}),
      ...(body.effect ? { effect: body.effect } : {}),
    };
  }
}

/**
 * Live first, recorded replay as the fallback.
 *
 * The console has to be demoable with nothing else running, but pretending to
 * be live when it is not would be the one unforgivable thing for a product
 * whose whole pitch is "we show you what we could not verify". So the fallback
 * is loud: the header chip flips to DEMO DATA, and the publish gate says in as
 * many words that confirming publishes nothing.
 */
export class ConsoleApi implements OnboardApi, AccountApi {
  private delegate: OnboardApi;
  private readonly demo = createDemoApi();
  private readonly live = new LiveApi();
  private onFallback: ((reason: string) => void) | null = null;

  constructor(forceDemo: boolean) {
    this.delegate = forceDemo ? this.demo : this.live;
  }

  get mode(): 'live' | 'demo' {
    return this.delegate.mode;
  }

  /** Set from the publish gate. Held in memory for this page only. */
  setPublishSecret(secret: string): void {
    this.live.publishSecret = secret;
  }

  whenFallback(fn: (reason: string) => void): void {
    this.onFallback = fn;
  }

  async start(url: string, ownership?: Ownership): Promise<StartResponse> {
    if (this.delegate === this.live) {
      try {
        return await this.live.start(url, ownership);
      } catch (err) {
        // The condition this fallback was written for, and the only one it may
        // still answer: THERE IS NO ONBOARDING API BEHIND THIS CONSOLE. That is
        // a 404 from a host serving the page but not the routes, or a transport
        // failure on a page where nothing has ever answered.
        //
        // Anything else is a live server saying no, and a refusal must reach
        // the reader as a refusal. Since a crawl needs an account, a signed-out
        // visitor now takes this path on every press — and demoting a 401, or a
        // reset from a proxy that mangled one, would drop them into a recorded
        // crawl of a site they never crawled and let them believe it.
        const noApiHere = err instanceof ApiError ? err.status === 404 : !this.live.reached;
        if (!noApiHere) throw err;
        this.delegate = this.demo;
        this.onFallback?.(err instanceof Error ? err.message : String(err));
      }
    }
    return this.delegate.start(url);
  }

  events(
    jobId: string,
    handlers: {
      onEvent: (e: ProgressEvent) => void;
      onDone: () => void;
      onError: (message: string) => void;
    },
  ): () => void {
    return this.delegate.events(jobId, handlers);
  }

  manifest(jobId: string): Promise<Manifest> {
    return this.delegate.manifest(jobId);
  }

  publish(jobId: string): Promise<PublishResponse> {
    return this.delegate.publish(jobId);
  }

  /**
   * Nobody is signed in to a replay. The recorded crawl exists so the console
   * can be shown with no server behind it, and the one thing that must not be
   * shown is a session that does not exist — so the gate renders "not signed
   * in" there, and the calls that would need a session say why they cannot.
   */
  private get noAccountService(): boolean {
    return this.delegate === this.demo;
  }

  private static readonly REPLAY =
    'This session is a replay of a recorded crawl; there is no account service here to answer.';

  async me(): Promise<Principal | null> {
    if (this.noAccountService) return null;
    try {
      return await this.live.me();
    } catch {
      // Unreachable is not signed in. The publish path will say the rest.
      return null;
    }
  }

  login(email: string, next?: string): Promise<LoginResponse> {
    if (this.noAccountService) return Promise.reject(new Error(ConsoleApi.REPLAY));
    return this.live.login(email, next);
  }

  ownership(tenantId: string): Promise<OwnershipStatus> {
    if (this.noAccountService) return Promise.reject(new Error(ConsoleApi.REPLAY));
    return this.live.ownership(tenantId);
  }

  challenge(tenantId: string): Promise<OwnershipChallenge> {
    if (this.noAccountService) return Promise.reject(new Error(ConsoleApi.REPLAY));
    return this.live.challenge(tenantId);
  }

  verify(tenantId: string, challengeId?: string): Promise<OwnershipVerification> {
    if (this.noAccountService) return Promise.reject(new Error(ConsoleApi.REPLAY));
    return this.live.verify(tenantId, challengeId);
  }
}

export function createApi(): ConsoleApi {
  const q = new URLSearchParams(location.search);
  const forced = q.has('demo') || q.has('mock') || q.get('api') === 'demo';
  return new ConsoleApi(forced);
}
